<template>
  <Teleport to="body">
    <div v-if="isVisible" class="modal-overlay" @click.stop>
      <div class="modal-content" @click.stop>
        <div class="header-container">
          <h2 class="modal-title">Registrar Pago</h2>
          <button class="modal-close-x" @click="$emit('close')">✕</button>
        </div>

        <!-- Resumen de la venta -->
        <div class="resumen-venta">
          <h3>Resumen de Venta</h3>
          <div class="resumen-grid">
            <div class="resumen-item">
              <span>Subtotal:</span>
              <span>L. {{ formatPrice(factura.sub_total) }}</span>
            </div>
            <div class="resumen-item">
              <span>Impuesto:</span>
              <span>L. {{ formatPrice(factura.total_ISV) }}</span>
            </div>
           <div class="resumen-item item-descuento">
              <span>Descuento:</span>
              <span>L. -{{ formatPrice(descuentoCorrect) }}</span>
            </div>
            <div class="resumen-item total">
              <span>Total:</span>
              <span>L. {{ formatPrice(total) }}</span>
            </div>
          </div>
        </div>

        <div class="form-container">
          <!-- Método de Pago -->
          <div class="form-group">
            <label>Método de Pago:</label>
            <select v-model="metodoPago" @change="handleMetodoPagoChange" :disabled="isModalLoading">
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Mixto">Mixto (Efectivo + Transferencia)</option>
              <option v-if="cuentasCobrarEnabled" value="Credito">Crédito</option>
            </select>
          </div>

          <!-- SECCIÓN: Validación y Selección de Cliente para Crédito -->
          <div v-if="metodoPago === 'Credito'" class="form-group credito-warning">
            <div class="warning-box">
              <h4>⚠️ VENTA A CRÉDITO</h4>
              
              <!-- Cliente ya seleccionado -->
              <div v-if="clienteEfectivo" class="cliente-seleccionado">
                <div class="cliente-info">
                  <div class="success-message">
                    ✅ {{ clienteEfectivo.nombre_completo }}
                  </div>
                  <div class="cliente-rtn">RTN: {{ clienteEfectivo.rtn }}</div>
                </div>
                <button 
                  type="button" 
                  @click="limpiarCliente" 
                  class="btn-cambiar-cliente"
                  :disabled="isModalLoading"
                >
                  Cambiar Cliente
                </button>
              </div>
              
              <!-- No hay cliente seleccionado -->
              <div v-else class="cliente-no-seleccionado">
                <p class="error-message">
                  Debe seleccionar un cliente para ventas a crédito.
                </p>
                <button 
                  type="button" 
                  @click="abrirSelectorCliente" 
                  class="btn-seleccionar-cliente"
                  :disabled="isModalLoading || cargandoClientes"
                >
                  {{ cargandoClientes ? 'Cargando...' : 'Seleccionar Cliente' }}
                </button>
              </div>
              
              <!-- Pago parcial adelanto -->
              <div class="adelanto-section">
                <label class="adelanto-label">Pago inicial (opcional):</label>
                <input
                  type="number"
                  v-model.number="montoAdelanto"
                  :max="total"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  class="adelanto-input"
                  :disabled="isModalLoading"
                  @input="validarAdelanto"
                />
                <div v-if="montoAdelanto > 0 && montoAdelanto < total" class="saldo-pendiente-info">
                  Saldo pendiente CxC: <strong>L. {{ formatPrice(total - montoAdelanto) }}</strong>
                </div>
                <div v-if="errorAdelanto" class="error-message">{{ errorAdelanto }}</div>
              </div>

              <!-- Panel de bloqueo de crédito con override admin -->
              <div v-if="mostrarBloqueoCredito" class="bloqueo-credito-panel">
                <div class="bloqueo-titulo">🚫 Crédito Bloqueado</div>
                <div class="bloqueo-razon">{{ creditoRazonBloqueo }}</div>
                <div class="bloqueo-override">
                  <label class="bloqueo-label">Contraseña de administrador para aprobar la venta:</label>
                  <input
                    v-model="claveAdmin"
                    type="password"
                    placeholder="Clave admin"
                    class="bloqueo-input"
                    ref="claveAdminInput"
                    @keyup.enter="aprobarConClave"
                    :disabled="verificandoClave"
                    autocomplete="off"
                  />
                  <div v-if="claveAdminError" class="error-message bloqueo-error">{{ claveAdminError }}</div>
                  <div class="bloqueo-btns">
                    <button
                      type="button"
                      @click="aprobarConClave"
                      :disabled="verificandoClave || !claveAdmin.trim()"
                      class="btn-aprobar-clave"
                    >
                      {{ verificandoClave ? 'Verificando...' : '🔑 Aprobar con Clave Admin' }}
                    </button>
                    <button
                      type="button"
                      @click="mostrarBloqueoCredito = false; claveAdmin = ''; claveAdminError = ''"
                      class="btn-cancelar-bloqueo"
                      :disabled="verificandoClave"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>

              <p class="credito-info">
                <small>La factura se generará inmediatamente y el saldo quedará como cuenta por cobrar.</small>
              </p>
            </div>

            <!-- Modal interno de selección de cliente -->
            <div v-if="mostrarSelectorCliente" class="selector-cliente-overlay" @click="cerrarSelectorCliente">
              <div class="selector-cliente-modal" @click.stop>
                <div class="selector-header">
                  <h4>Seleccionar Cliente</h4>
                  <button @click="cerrarSelectorCliente" class="btn-close">&times;</button>
                </div>
                
                <!-- Búsqueda de cliente -->
                <div class="busqueda-cliente">
                  <input
                    ref="busquedaClienteInput"
                    type="text"
                    v-model="busquedaCliente"
                    placeholder="Buscar por nombre o RTN..."
                    class="input-busqueda"
                    :disabled="cargandoClientes"
                  />
                </div>

                <!-- Lista de clientes -->
                <div class="lista-clientes">
                  <!-- Indicador de carga -->
                  <div v-if="cargandoClientes" class="cargando-clientes">
                    <div class="spinner"></div>
                    <span>Cargando clientes...</span>
                  </div>
                  
                  <!-- Lista normal cuando no está cargando -->
                  <template v-else>
                    <div 
                      v-for="cliente in clientesFiltrados" 
                      :key="cliente.id_cliente"
                      @click="seleccionarCliente(cliente)"
                      class="cliente-item"
                    >
                      <div class="cliente-nombre">{{ cliente.nombre_completo }}</div>
                      <div class="cliente-rtn-small">{{ cliente.rtn }}</div>
                    </div>
                    
                    <div v-if="clientesFiltrados.length === 0 && !cargandoClientes" class="no-clientes">
                      No se encontraron clientes
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Pago en Efectivo -->
          <div v-if="metodoPago === 'Efectivo'" class="form-group">
            <label>Recibido del cliente:</label>
            <input
              type="number"
              v-model.number="montoEfectivo"
              ref="montoInput"
              @input="calcularCambio"
              min="0"
              step="0.01"
              :disabled="isModalLoading"
            />
            <div class="billetes-rapidos">
              <button type="button" class="btn-billete" @click="setBillete(50)"  :disabled="isModalLoading">L. 50</button>
              <button type="button" class="btn-billete" @click="setBillete(100)" :disabled="isModalLoading">L. 100</button>
              <button type="button" class="btn-billete" @click="setBillete(200)" :disabled="isModalLoading">L. 200</button>
              <button type="button" class="btn-billete" @click="setBillete(500)" :disabled="isModalLoading">L. 500</button>
              <button type="button" class="btn-billete btn-exacto" @click="setBillete(total)" :disabled="isModalLoading">Exacto</button>
            </div>
            <div v-if="errorEfectivo" class="error-message">
              {{ errorEfectivo }}
            </div>
            <div v-if="faltanteEfectivo > 0" class="faltante-message">
              Falta: L. {{ formatPrice(faltanteEfectivo) }}
            </div>
            <div v-if="montoEfectivo > 0 && faltanteEfectivo > 0" class="parcial-hint">
              <button type="button" class="btn-parcial" @click="convertirAParcial" :disabled="isModalLoading">
                <i class="bi bi-arrow-right-circle"></i>
                Registrar L. {{ formatPrice(montoEfectivo) }} como adelanto y L. {{ formatPrice(faltanteEfectivo) }} a crédito
              </button>
            </div>
          </div>

          <!-- Pago por Transferencia -->
          <div v-if="metodoPago === 'Transferencia'" class="form-group">
            <label>Monto de la transferencia:</label>
            <input type="number" v-model.number="montoTransferencia" readonly />
            <label>Número de transferencia:</label>
            <input
              type="text"
              v-model="numeroTransferencia"
              maxlength="5"
              pattern="\d{5}"
              placeholder="12345"
              :disabled="isModalLoading"
            />
            <div v-if="errorTransferencia" class="error-message">
              {{ errorTransferencia }}
            </div>
          </div>

          <!-- Pago Mixto -->
          <div v-if="metodoPago === 'Mixto'" class="form-group">
            <div class="mixto-header">
              <h4>Distribuir Total: L. {{ formatPrice(total) }}</h4>
            </div>
            
            <label>Monto en Efectivo:</label>
            <input
              type="number"
              v-model.number="montoEfectivoMixto"
              @input="calcularMontosMixtos"
              :min="0.01"
              :max="total - 0.01"
              step="0.01"
              :disabled="isModalLoading"
              placeholder="0.00"
            />

            <label>Monto por Transferencia:</label>
            <input
              type="number"
              v-model.number="montoTransferenciaMixto"
              @input="calcularMontosMixtosDesdeTransfer"
              :min="0.01"
              :max="total - 0.01"
              step="0.01"
              :disabled="isModalLoading"
              placeholder="0.00"
            />

            <label>Número de transferencia:</label>
            <input
              type="text"
              v-model="numeroTransferenciaMixto"
              maxlength="5"
              pattern="\d{5}"
              placeholder="12345"
              :disabled="isModalLoading"
            />

            <!-- Resumen del pago mixto -->
            <div class="mixto-resumen">
              <div class="mixto-item">
                <span>Efectivo:</span>
                <span>L. {{ formatPrice(montoEfectivoMixto || 0) }}</span>
              </div>
              <div class="mixto-item">
                <span>Transferencia:</span>
                <span>L. {{ formatPrice(montoTransferenciaMixto || 0) }}</span>
              </div>
              <div class="mixto-item total-mixto">
                <span>Total Pagado:</span>
                <span>L. {{ formatPrice(totalPagoMixto) }}</span>
              </div>
              <div class="mixto-item" v-if="faltanteMixto > 0">
                <span style="color: #dc3545;">Faltante:</span>
                <span style="color: #dc3545;">L. {{ formatPrice(faltanteMixto) }}</span>
              </div>
            </div>

            <div v-if="errorMixto" class="error-message">
              {{ errorMixto }}
            </div>
          </div>

          <!-- Cambio (solo para efectivo) -->
          <div v-if="mostrarCambio" class="cambio-section cambio-grande">
            <div class="cambio-label">Cambio a devolver</div>
            <div class="cambio-monto">L. {{ formatPrice(cambio) }}</div>
          </div>

          <!-- Notas -->
          <div class="form-group">
            <label>Notas:</label>
            <textarea
              v-model="notas"
              placeholder="Observaciones adicionales..."
              :disabled="isModalLoading"
            ></textarea>
          </div>
        </div>

        <div class="button-container">
          <button
            @click="confirmarPago"
            :disabled="!isPagoValido || isModalLoading"
            class="btn-confirm"
            :class="{ 'btn-credito': metodoPago === 'Credito' }"
          >
            {{ textoBoton }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
// Importar la función para obtener clientes
import { getClientesbyEmpresa, getEstadoCredito, postVerificarAdminClave } from "../../../services/clienteSolicitudes";
import { isRouteEnabled } from "@/utils/planUtils";

export default {
  name: "RegistrarPagoModal",
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    factura: {
      type: Object,
      required: true,
    },
    clienteSeleccionado: {
      type: Object,
      default: null,
    },
    id_usuario: {
      type: Number,
      required: true,
    },
  },
  emits: ['close', 'confirm-payment', 'modal-focused'],
  data() {
    return {
      isModalLoading: false,
      metodoPago: "Efectivo",
      montoEfectivo: 0,
      montoTransferencia: 0,
      numeroTransferencia: "",
      montoEfectivoMixto: 0,
      montoTransferenciaMixto: 0,
      numeroTransferenciaMixto: "",
      notas: "",
      cambio: 0,
      errorEfectivo: "",
      errorTransferencia: "",
      errorMixto: "",
      // Variables para cliente
      clienteSeleccionadoModal: null,
      mostrarSelectorCliente: false,
      busquedaCliente: "",
      clientes: [],
      cargandoClientes: false,
      // Pago parcial
      montoAdelanto: 0,
      errorAdelanto: "",
      // Flag: el usuario limpió el cliente manualmente (invalida la prop del padre)
      clienteLimpiado: false,
      // Validación de crédito
      creditoEstado: null,
      creditoAprobado: false,
      mostrarBloqueoCredito: false,
      creditoRazonBloqueo: "",
      claveAdmin: "",
      claveAdminError: "",
      verificandoClave: false,
    };
  },
  computed: {
    cuentasCobrarEnabled() { return isRouteEnabled('/cuentas-por-cobrar'); },
    total() {
      return this.factura.total;
    },
    
    descuentoCorrect() {
      if (this.factura.descuento > 30 && this.factura.descuento < 35) {
        return 30.00;
      }
      return this.factura.descuento;
    },
    
    mostrarCambio() {
      return this.metodoPago === "Efectivo" && this.cambio > 0;
    },

    faltanteEfectivo() {
      if (this.metodoPago !== "Efectivo" || this.montoEfectivo <= 0) return 0;
      return Math.max(0, this.total - this.montoEfectivo);
    },

    totalPagoMixto() {
      return (this.montoEfectivoMixto || 0) + (this.montoTransferenciaMixto || 0);
    },

    faltanteMixto() {
      return Math.max(0, this.total - this.totalPagoMixto);
    },

    textoBoton() {
      if (this.isModalLoading) {
        return this.metodoPago === 'Credito' ? "Registrando a Crédito..." : "Procesando...";
      }
      if (this.metodoPago === 'Credito') {
        return this.montoAdelanto > 0
          ? `Registrar Crédito (L. ${this.formatPrice(this.montoAdelanto)} adelanto)`
          : "Registrar a Crédito";
      }
      return "Confirmar Pago";
    },

    clienteEfectivo() {
      // Si el usuario limpió explícitamente, ignorar la prop del padre
      if (this.clienteLimpiado) return this.clienteSeleccionadoModal || null;
      return this.clienteSeleccionadoModal || this.clienteSeleccionado;
    },

    clientesFiltrados() {
      if (!this.busquedaCliente) return this.clientes;
      
      return this.clientes.filter(cliente => 
        cliente.nombre_completo.toLowerCase().includes(this.busquedaCliente.toLowerCase()) ||
        cliente.rtn.includes(this.busquedaCliente)
      );
    },
    
    isPagoValido() {
      switch (this.metodoPago) {
        case "Efectivo":
          return this.montoEfectivo > 0 && this.montoEfectivo >= this.total;
        case "Transferencia":
          return (
            this.montoTransferencia === this.total &&
            this.numeroTransferencia?.length === 5
          );
        case "Mixto":
          return (
            this.totalPagoMixto >= this.total &&
            this.montoEfectivoMixto > 0 &&
            this.montoTransferenciaMixto > 0 &&
            this.numeroTransferenciaMixto?.length === 5
          );
        case "Credito":
          return this.clienteEfectivo !== null && !this.errorAdelanto && !this.mostrarBloqueoCredito;
        default:
          return false;
      }
    },
  },
  
  methods: {
    formatPrice(value) {
      return Number(value).toFixed(2);
    },

    setBillete(monto) {
      this.montoEfectivo = monto;
      this.calcularCambio();
      this.$nextTick(() => this.$refs.montoInput?.focus());
    },

    calcularCambio() {
      if (this.metodoPago === "Efectivo") {
        this.cambio = Math.max(0, this.montoEfectivo - this.total);
        this.errorEfectivo = "";
      }
    },

    calcularMontosMixtos() {
      if (this.montoEfectivoMixto >= this.total) {
        this.montoEfectivoMixto = this.total - 0.01;
      }
      
      this.montoTransferenciaMixto = this.total - (this.montoEfectivoMixto || 0);
      
      if (this.montoTransferenciaMixto < 0) {
        this.montoTransferenciaMixto = 0;
      }
      
      this.validarPagoMixto();
    },

    calcularMontosMixtosDesdeTransfer() {
      if (this.montoTransferenciaMixto >= this.total) {
        this.montoTransferenciaMixto = this.total - 0.01;
      }
      
      this.montoEfectivoMixto = this.total - (this.montoTransferenciaMixto || 0);
      
      if (this.montoEfectivoMixto < 0) {
        this.montoEfectivoMixto = 0;
      }
      
      this.validarPagoMixto();
    },

    validarPagoMixto() {
      this.errorMixto = "";
      
      if (this.montoEfectivoMixto <= 0) {
        this.errorMixto = "El monto en efectivo debe ser mayor a 0";
        return;
      }
      
      if (this.montoTransferenciaMixto <= 0) {
        this.errorMixto = "El monto de transferencia debe ser mayor a 0";
        return;
      }
      
      if (this.totalPagoMixto < this.total) {
        this.errorMixto = `Falta L. ${this.formatPrice(this.faltanteMixto)} para completar el pago`;
        return;
      }
      
      if (this.totalPagoMixto > this.total) {
        this.errorMixto = `El total excede en L. ${this.formatPrice(this.totalPagoMixto - this.total)}`;
        return;
      }
    },

    // Métodos para cliente
    async cargarClientes() {
      if (this.clientes.length > 0) return; // Ya están cargados
      
      this.cargandoClientes = true;
      try {
        this.clientes = await getClientesbyEmpresa(this.id_usuario);
      } catch (error) {
        console.error('Error al cargar clientes:', error);
        // Mostrar error al usuario si es necesario
      } finally {
        this.cargandoClientes = false;
      }
    },

    seleccionarCliente(cliente) {
      this.clienteSeleccionadoModal = cliente;
      this.clienteLimpiado = false; // Ya hay selección explícita, desactivar flag
      this.mostrarSelectorCliente = false;
      this.busquedaCliente = "";
    },

    async abrirSelectorCliente() {
      // Cargar clientes antes de abrir el selector
      await this.cargarClientes();
      
      this.mostrarSelectorCliente = true;
      this.$nextTick(() => {
        this.$refs.busquedaClienteInput?.focus();
      });
    },

    cerrarSelectorCliente() {
      this.mostrarSelectorCliente = false;
      this.busquedaCliente = "";
    },

    limpiarCliente() {
      this.clienteSeleccionadoModal = null;
      this.clienteLimpiado = true; // Invalidar la prop del padre
      this.abrirSelectorCliente();  // Abrir selector directamente para elegir otro
    },

    async convertirAParcial() {
      const adelanto = this.montoEfectivo;
      this.metodoPago  = 'Credito';
      this.montoAdelanto = adelanto;
      this.montoEfectivo = 0;
      this.cambio = 0;
      this.errorAdelanto = '';
      if (!this.clienteEfectivo) {
        await this.abrirSelectorCliente();
      }
    },

    validarAdelanto() {
      this.errorAdelanto = "";
      if (this.montoAdelanto < 0) {
        this.montoAdelanto = 0;
      } else if (this.montoAdelanto > this.total) {
        this.errorAdelanto = `El adelanto no puede superar el total (L. ${this.formatPrice(this.total)})`;
      }
    },

    async handleMetodoPagoChange() {
      // Resetear valores
      this.montoEfectivo = 0;
      this.cambio = 0;
      this.numeroTransferencia = "";
      this.montoEfectivoMixto = 0;
      this.montoTransferenciaMixto = 0;
      this.numeroTransferenciaMixto = "";
      this.errorEfectivo = "";
      this.errorTransferencia = "";
      this.errorMixto = "";
      this.montoAdelanto = 0;
      this.errorAdelanto = "";

      // Si cambia a crédito y no hay cliente, cargar clientes y abrir selector
      if (this.metodoPago === "Credito" && !this.clienteEfectivo) {
        await this.abrirSelectorCliente();
      }

      // Configurar valores iniciales según método
      switch (this.metodoPago) {
        case "Efectivo":
          this.montoEfectivo = this.total;
          this.montoTransferencia = 0;
          break;
        case "Transferencia":
          this.montoTransferencia = this.total;
          this.montoEfectivo = 0;
          break;
        case "Mixto":
          this.montoEfectivoMixto = Math.round((this.total / 2) * 100) / 100;
          this.montoTransferenciaMixto = this.total - this.montoEfectivoMixto;
          break;
        case "Credito":
          // No necesita montos para crédito
          break;
      }
    },
    
    async confirmarPago() {
      if (!this.isPagoValido || this.isModalLoading) return;

      // ── Validación de crédito (solo la primera vez, no si ya fue aprobado por admin) ──
      if (this.metodoPago === 'Credito' && this.clienteEfectivo && !this.creditoAprobado) {
        this.isModalLoading = true;
        try {
          const estado = await getEstadoCredito(this.clienteEfectivo.id_cliente, this.id_usuario);
          this.creditoEstado = estado;

          const totalConNueva = estado.saldo_pendiente + this.total;
          const excedeLimite  = estado.tiene_limite && totalConNueva > estado.limite_credito;
          const tieneVencidas = estado.facturas_vencidas.length > 0;

          if (excedeLimite || tieneVencidas) {
            const razones = [];
            if (tieneVencidas) {
              const tv  = estado.facturas_vencidas.length;
              const max = estado.facturas_vencidas.reduce((m, f) => Math.max(m, f.dias_vencido), 0);
              razones.push(`${tv} factura${tv > 1 ? 's' : ''} vencida${tv > 1 ? 's' : ''} (máx. ${max} días)`);
            }
            if (excedeLimite) {
              razones.push(
                `Excede límite de L. ${this.formatPrice(estado.limite_credito)} ` +
                `(saldo actual: L. ${this.formatPrice(estado.saldo_pendiente)})`
              );
            }
            this.creditoRazonBloqueo  = razones.join(' · ');
            this.mostrarBloqueoCredito = true;
            this.isModalLoading       = false;
            this.$nextTick(() => this.$refs.claveAdminInput?.focus());
            return; // Esperamos la aprobación del administrador
          }
        } catch (e) {
          console.error('Error verificando crédito:', e);
          this.isModalLoading = false;
          return;
        }
        this.isModalLoading = false;
      }

      // ── Proceder con el pago ──
      this.isModalLoading = true;
      try {
        const datosPago = {
          metodoPago: this.metodoPago,
          montoEfectivo: this.metodoPago === "Mixto" ? this.montoEfectivoMixto : this.montoEfectivo,
          montoTransferencia: this.metodoPago === "Mixto" ? this.montoTransferenciaMixto : this.montoTransferencia,
          numeroTransferencia: this.metodoPago === "Mixto" ? this.numeroTransferenciaMixto : this.numeroTransferencia,
          cambio: this.cambio,
          notas: this.notas,
          clienteSeleccionado: this.clienteEfectivo,
          montoAdelanto: this.montoAdelanto || 0,
        };

        this.$emit("confirm-payment", datosPago);

      } catch (error) {
        console.error("Error al procesar el pago:", error);
        this.isModalLoading = false;
      }
    },

    async aprobarConClave() {
      if (!this.claveAdmin.trim()) {
        this.claveAdminError = 'Ingrese la clave de administrador';
        return;
      }
      this.verificandoClave = true;
      this.claveAdminError  = '';
      try {
        const resp = await postVerificarAdminClave(this.id_usuario, this.claveAdmin);
        if (resp.isValid) {
          this.creditoAprobado       = true;
          this.mostrarBloqueoCredito = false;
          this.claveAdmin            = '';
          this.claveAdminError       = '';
          // Ahora creditoAprobado=true → confirmarPago() salta la verificación
          await this.confirmarPago();
        } else {
          this.claveAdminError = '❌ Clave incorrecta. Intente de nuevo.';
        }
      } catch (e) {
        this.claveAdminError = 'Error al verificar la clave. Intente de nuevo.';
      } finally {
        this.verificandoClave = false;
      }
    },

    cerrarModalDesdePadre() {
      this.isModalLoading = false;
      this.$emit('modal-focused', false);
      this.$emit('close');
    },

    resetForm() {
      this.metodoPago = "Efectivo";
      this.montoEfectivo = this.total;
      this.montoTransferencia = 0;
      this.numeroTransferencia = "";
      this.montoEfectivoMixto = 0;
      this.montoTransferenciaMixto = 0;
      this.numeroTransferenciaMixto = "";
      this.notas = "";
      this.cambio = 0;
      this.errorEfectivo = "";
      this.errorTransferencia = "";
      this.errorMixto = "";
      this.isModalLoading = false;
      this.clienteSeleccionadoModal = null;
      this.clienteLimpiado = false;
      this.mostrarSelectorCliente = false;
      this.busquedaCliente = "";
      this.montoAdelanto = 0;
      this.errorAdelanto = "";
      this.creditoEstado = null;
      this.creditoAprobado = false;
      this.mostrarBloqueoCredito = false;
      this.creditoRazonBloqueo = "";
      this.claveAdmin = "";
      this.claveAdminError = "";
      this.verificandoClave = false;
    }
  },
  
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$emit('modal-focused', true);
        this.resetForm();
        this.calcularCambio();
        this.$nextTick(() => {
          this.$refs.montoInput?.focus();
        });
      } else {
        this.$emit('modal-focused', false);
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
  font-family: "Montserrat", sans-serif;
}

.resumen-venta {
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 20px;
}

.resumen-grid {
  display: grid;
  gap: 10px;
}

.resumen-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
}

.item-descuento {
  color: rgb(194, 30, 30);
}

.resumen-item.total {
  border-top: 2px solid #ddd;
  margin-top: 10px;
  padding-top: 10px;
  font-weight: bold;
  font-size: 1.2em;
}

/* ESTILOS PARA PAGO MIXTO */
.mixto-header {
  background-color: #e3f2fd;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  text-align: center;
}

.mixto-header h4 {
  margin: 0;
  color: #1565c0;
  font-weight: 600;
}

.mixto-resumen {
  background-color: #f5f5f5;
  border-radius: 5px;
  padding: 15px;
  margin-top: 15px;
}

.mixto-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #ddd;
}

.mixto-item:last-child {
  border-bottom: none;
}

.total-mixto {
  font-weight: bold;
  background-color: #e8f5e9;
  padding: 10px;
  margin: 10px -15px 0 -15px;
  border-radius: 0 0 5px 5px;
}

/* ESTILOS PARA CRÉDITO */
.credito-warning {
  margin: 15px 0;
}

.warning-box {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 5px;
  padding: 15px;
}

.warning-box h4 {
  margin: 0 0 15px 0;
  color: #856404;
  font-size: 16px;
}

.cliente-seleccionado {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
}

.cliente-info {
  flex: 1;
}

.cliente-rtn {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.cliente-no-seleccionado {
  margin: 10px 0;
}

.credito-info {
  margin: 10px 0 0 0;
}

.credito-info small {
  color: #6c757d;
  font-style: italic;
}

.parcial-hint {
  margin-top: 8px;
}

.btn-parcial {
  width: 100%;
  padding: 8px 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  color: #856404;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.btn-parcial:hover:not(:disabled) { background: #ffe69c; }
.btn-parcial:disabled { opacity: 0.5; cursor: not-allowed; }

.adelanto-section {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #d4a600;
}

.adelanto-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 4px;
}

.adelanto-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d4a600;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.saldo-pendiente-info {
  margin-top: 6px;
  font-size: 13px;
  color: #495057;
  background: rgba(255,255,255,0.6);
  border-radius: 4px;
  padding: 4px 8px;
}

.success-message {
  color: #155724;
  font-weight: 500;
  font-size: 14px;
}

.btn-cambiar-cliente,
.btn-seleccionar-cliente {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-left: 10px;
}

.btn-cambiar-cliente:hover,
.btn-seleccionar-cliente:hover {
  background-color: #0056b3;
}

.btn-cambiar-cliente:disabled,
.btn-seleccionar-cliente:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* ESTILOS PARA SELECTOR DE CLIENTE */
.selector-cliente-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.selector-cliente-modal {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #ddd;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.selector-header h4 {
  margin: 0;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #000;
  background-color: #f0f0f0;
  border-radius: 50%;
}

.busqueda-cliente {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.input-busqueda {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.input-busqueda:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 3px rgba(0, 123, 255, 0.3);
}

.lista-clientes {
  max-height: 300px;
  overflow-y: auto;
  padding: 10px 0;
}

.cliente-item {
  padding: 12px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.cliente-item:hover {
  background-color: #f8f9fa;
}

.cliente-item:last-child {
  border-bottom: none;
}

.cliente-nombre {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.cliente-rtn-small {
  font-size: 12px;
  color: #666;
}

.no-clientes {
  padding: 20px;
  text-align: center;
  color: #666;
  font-style: italic;
}

/* Estilos para carga de clientes */
.cargando-clientes {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  color: #666;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cambio-section {
  background-color: #e8f5e9;
  border-radius: 8px;
  margin-top: 10px;
}

.cambio-grande {
  padding: 16px 20px;
  border: 2px solid #4caf50;
  text-align: center;
  animation: cambio-pop .2s ease-out;
}

.cambio-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #2e7d32;
  margin-bottom: 4px;
}

.cambio-monto {
  font-size: 2rem;
  font-weight: 800;
  color: #1b5e20;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

@keyframes cambio-pop {
  0%   { transform: scale(.95); opacity: .7; }
  100% { transform: scale(1);   opacity: 1;  }
}

.billetes-rapidos {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.btn-billete {
  padding: 6px 12px;
  background: #f0f4ff;
  border: 1.5px solid #c7d2fe;
  border-radius: 6px;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  min-width: auto;
  transition: background .15s, border-color .15s;
}

.btn-billete:hover:not(:disabled) {
  background: #e0e7ff;
  border-color: #818cf8;
}

.btn-billete:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.btn-exacto {
  background: #f0fdf4;
  border-color: #86efac;
  color: #166534;
}

.btn-exacto:hover:not(:disabled) {
  background: #dcfce7;
  border-color: #4ade80;
}

.faltante-message {
  color: #dc3545;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
}

.btn-confirm {
  background-color: #4caf50;
  width: 100%;
}

.btn-confirm:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.btn-credito {
  background-color: #fd7e14 !important;
  color: white !important;
}

.btn-credito:hover:not(:disabled) {
  background-color: #e8650e !important;
}

input[type="text"],
input[type="number"],
select,
textarea {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #4caf50;
  outline: none;
  box-shadow: 0 0 3px rgba(76, 175, 80, 0.3);
}

input:disabled,
select:disabled,
textarea:disabled {
  background-color: #f5f5f5;
  color: #666;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 2vh 0;
  z-index: 1000;
  overflow-y: auto;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
  width: 700px;
  max-width: 90vw;
  max-height: 96vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1010;
  margin: auto;
}

.modal-content::-webkit-scrollbar {
  width: 8px;
}

.modal-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
 background: #666;
}

.button-container {
 display: flex;
 justify-content: center;
 margin-top: 20px;
 padding-top: 15px;
 border-top: 1px solid #ddd;
 position: sticky;
 bottom: 0;
 background: white;
 padding-bottom: 5px;
}

.modal-title {
 font-size: 24px;
 font-weight: bold;
 margin-bottom: 10px;
 text-align: center;
}

.header-container {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 20px;
}


.form-container {
 display: flex;
 flex-direction: column;
 margin-bottom: 20px;
 gap: 15px;
}

.form-group {
 display: flex;
 flex-direction: column;
 gap: 12px;
 margin-bottom: 20px;
}

.form-group label {
 margin-bottom: 5px;
 text-align: left;
 font-weight: 500;
}

.form-group input,
.form-group select,
.form-group textarea {
 padding: 10px;
 border: 1px solid #ccc;
 border-radius: 5px;
 width: 95%;
 background-color: white;
}

button {
 padding: 10px 20px;
 border: none;
 border-radius: 5px;
 background-color: #4caf50;
 color: #fff;
 cursor: pointer;
 min-width: 140px;
}

button:hover:not(:disabled) {
 background-color: #3e8e41;
}

textarea {
 resize: vertical;
 min-height: 60px;
 max-height: 150px;
}

input[type="number"] {
 -moz-appearance: textfield;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
 -webkit-appearance: none;
 margin: 0;
}

.error-message {
 color: #dc3545;
 font-size: 0.875rem;
 margin-top: 0.25rem;
 font-weight: 500;
}

input:read-only {
 background-color: #e9ecef;
 cursor: not-allowed;
}

.btn-confirm:disabled {
 background-color: #6c757d;
 cursor: not-allowed;
}

/* Modo oscuro */
.dark .modal-content {
 background: #1a1a1a;
 border: 1px solid #2b2b2b;
 color: #e5e5e5;
}

.dark .resumen-venta {
 background-color: #2b2b2b;
}

.dark .mixto-header {
 background-color: #1a2f3a;
}

.dark .mixto-header h4 {
 color: #00bcd4;
}

.dark .mixto-resumen {
 background-color: #2b2b2b;
}

.dark .total-mixto {
 background-color: #1a2f1a;
}

.dark .cambio-section {
  background-color: #1a2f1a;
}

.dark .cambio-grande {
  border-color: #22c55e;
}

.dark .cambio-label {
  color: #4ade80;
}

.dark .cambio-monto {
  color: #86efac;
}

.dark .btn-billete {
  background: #1e1b4b;
  border-color: #4338ca;
  color: #a5b4fc;
}

.dark .btn-billete:hover:not(:disabled) {
  background: #312e81;
  border-color: #6366f1;
}

.dark .btn-exacto {
  background: #052e16;
  border-color: #16a34a;
  color: #4ade80;
}

.dark .btn-exacto:hover:not(:disabled) {
  background: #14532d;
  border-color: #22c55e;
}

.dark .faltante-message {
  color: #f87171;
}

.dark .warning-box {
 background-color: #2b2b1a;
 border-color: #3d3d1a;
}

.dark .warning-box h4 {
 color: #ffc107;
}

.dark .selector-cliente-modal {
 background: #1a1a1a;
 color: #e5e5e5;
}

.dark .selector-header {
 background-color: #2b2b2b;
 border-color: #3d3d3d;
}

.dark .selector-header h4 {
 color: #e5e5e5;
}

.dark .btn-close {
 color: #e5e5e5;
}

.dark .btn-close:hover {
 background-color: #3d3d3d;
 color: #fff;
}

.dark .input-busqueda {
 background-color: #2b2b2b;
 border-color: #3d3d3d;
 color: #e5e5e5;
}

.dark .input-busqueda:focus {
 border-color: #00ff95;
 box-shadow: 0 0 3px rgba(0, 255, 149, 0.3);
}

.dark .cliente-item {
 border-color: #3d3d3d;
}

.dark .cliente-item:hover {
 background-color: #2b2b2b;
}

.dark .cliente-nombre {
 color: #e5e5e5;
}

.dark .cliente-rtn-small {
 color: #999;
}

.dark .no-clientes {
 color: #999;
}

.dark .btn-cambiar-cliente,
.dark .btn-seleccionar-cliente {
 background-color: #007bff;
}

.dark .btn-cambiar-cliente:hover,
.dark .btn-seleccionar-cliente:hover {
 background-color: #0056b3;
}

.dark input,
.dark select,
.dark textarea {
 background-color: #141414;
 border-color: #2b2b2b;
 color: #e5e5e5;
}

.dark input:disabled,
.dark select:disabled,
.dark textarea:disabled {
 background-color: #0f0f0f;
 color: #666;
}

.dark .btn-confirm {
 background-color: #00ff95;
 color: #141414;
}

.dark .btn-confirm:hover:not(:disabled) {
 background-color: #00ffaa;
}

.dark .btn-credito {
 background-color: #fd7e14 !important;
 color: white !important;
}

.dark .btn-credito:hover:not(:disabled) {
 background-color: #e8650e !important;
}

/* ── Panel de bloqueo de crédito ── */
.bloqueo-credito-panel {
  margin-top: 14px;
  background: #fff0f0;
  border: 2px solid #dc3545;
  border-radius: 8px;
  padding: 14px;
}

.bloqueo-titulo {
  font-size: 15px;
  font-weight: 700;
  color: #c0392b;
  margin-bottom: 6px;
}

.bloqueo-razon {
  font-size: 13px;
  color: #721c24;
  margin-bottom: 12px;
  background: rgba(220, 53, 69, 0.08);
  border-radius: 4px;
  padding: 6px 10px;
  line-height: 1.5;
}

.bloqueo-override {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bloqueo-label {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.bloqueo-input {
  width: 100%;
  padding: 8px 10px;
  border: 1.5px solid #dc3545;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  box-sizing: border-box;
}

.bloqueo-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
}

.bloqueo-error {
  margin-top: 0;
}

.bloqueo-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-aprobar-clave {
  flex: 1;
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 180px;
  transition: background 0.15s;
}

.btn-aprobar-clave:hover:not(:disabled) { background: #c0392b; }
.btn-aprobar-clave:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-cancelar-bloqueo {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  min-width: auto;
  transition: background 0.15s;
}

.btn-cancelar-bloqueo:hover:not(:disabled) { background: #545b62; }
.btn-cancelar-bloqueo:disabled { opacity: 0.5; cursor: not-allowed; }

/* Modo oscuro — bloqueo de crédito */
.dark .bloqueo-credito-panel {
  background: #2a1010;
  border-color: #e57373;
}

.dark .bloqueo-titulo { color: #ef9a9a; }
.dark .bloqueo-razon  { color: #ffcdd2; background: rgba(229, 115, 115, 0.12); }
.dark .bloqueo-label  { color: #ccc; }

.dark .bloqueo-input {
  background: #1a1a1a;
  border-color: #e57373;
  color: #e5e5e5;
}

.dark .bloqueo-input:focus {
  border-color: #ef9a9a;
  box-shadow: 0 0 0 3px rgba(229, 115, 115, 0.25);
}
</style>

<style>
/* ── Dark mode global (Teleport escapa de app-wrapper) ── */
html.dark .modal-content { background: #1a1a1a !important; border: 1px solid #2b2b2b; color: #e5e5e5 !important; }
html.dark .modal-title { color: #c09d62; }
html.dark .modal-close-x { background: transparent !important; color: #9ca3af !important; border: none; cursor: pointer; }
html.dark .modal-close-x:hover { color: #e5e7eb !important; }

html.dark .resumen-venta { background-color: #2b2b2b; color: #e5e5e5; }
html.dark .resumen-venta h3 { color: #c09d62; }
html.dark .resumen-item { color: #d1d5db; }
html.dark .resumen-item.total { color: #c09d62; font-weight: bold; }
html.dark .item-descuento { color: #f87171; }

html.dark .mixto-header { background-color: #1a2f3a; }
html.dark .mixto-header h4 { color: #00bcd4; }
html.dark .mixto-resumen { background-color: #2b2b2b; }
html.dark .total-mixto { background-color: #1a2f1a; }

html.dark .cambio-section { background-color: #1a2f1a; }
html.dark .cambio-grande { border-color: #22c55e; }
html.dark .cambio-label { color: #4ade80; }
html.dark .cambio-monto { color: #86efac; }

html.dark .btn-billete { background: #1e1b4b; border-color: #4338ca; color: #a5b4fc; }
html.dark .btn-billete:hover:not(:disabled) { background: #312e81; border-color: #6366f1; }
html.dark .btn-exacto { background: #052e16; border-color: #16a34a; color: #4ade80; }
html.dark .btn-exacto:hover:not(:disabled) { background: #14532d; border-color: #22c55e; }
html.dark .btn-confirm { background-color: #198754 !important; color: #fff !important; }
html.dark .btn-confirm:hover:not(:disabled) { background-color: #146c43 !important; }
html.dark .btn-credito { background-color: #fd7e14 !important; color: white !important; }
html.dark .btn-credito:hover:not(:disabled) { background-color: #e8650e !important; }

html.dark .faltante-message { color: #f87171; }
html.dark .warning-box { background-color: #2b2b1a; border-color: #3d3d1a; }
html.dark .warning-box h4 { color: #ffc107; }

html.dark .selector-cliente-modal { background: #1a1a1a; color: #e5e5e5; }
html.dark .selector-header { background-color: #2b2b2b; border-color: #3d3d3d; }
html.dark .selector-header h4 { color: #e5e5e5; }
html.dark .input-busqueda { background-color: #2b2b2b; border-color: #3d3d3d; color: #e5e5e5; }
html.dark .cliente-item { border-color: #3d3d3d; }
html.dark .cliente-item:hover { background-color: #2b2b2b; }
html.dark .cliente-nombre { color: #e5e5e5; }
html.dark .cliente-rtn-small { color: #999; }
html.dark .no-clientes { color: #999; }
html.dark .btn-cambiar-cliente, html.dark .btn-seleccionar-cliente { background-color: #007bff; }
html.dark .btn-cambiar-cliente:hover, html.dark .btn-seleccionar-cliente:hover { background-color: #0056b3; }

html.dark .button-container {
  background: #1a1a1a !important;
  border-top-color: #2b2b2b !important;
}

html.dark .modal-content input,
html.dark .modal-content select,
html.dark .modal-content textarea { background-color: #141414; border-color: #2b2b2b; color: #e5e5e5; }
html.dark .modal-content input:disabled,
html.dark .modal-content select:disabled { background-color: #0f0f0f; color: #666; }
html.dark .modal-content label { color: #9ca3af; }

html.dark .bloqueo-credito-panel { background: #2a1010; border-color: #e57373; }
html.dark .bloqueo-titulo { color: #ef9a9a; }
html.dark .bloqueo-razon { color: #ffcdd2; background: rgba(229,115,115,0.12); }
html.dark .bloqueo-label { color: #ccc; }
html.dark .bloqueo-input { background: #1a1a1a; border-color: #e57373; color: #e5e5e5; }
html.dark .bloqueo-input:focus { border-color: #ef9a9a; box-shadow: 0 0 0 3px rgba(229,115,115,0.25); }
</style>